<template>
  <div>
    <LabeledText v-if="customer" :label="t('relatedCustomer')" class="mb-4">
      <p>{{ `${t('customerNumber')} ${customer.customerNumber}` }}</p>
      <p v-if="customer.company">{{ customer.company }}</p>
      <p>{{ customer.firstName }} {{ customer.lastName }}</p>
      <p>{{ customer.street }}</p>
      <p>{{ customer.zip }} {{ customer.city }}</p>
      <p>{{ customer.country }}</p>
    </LabeledText>
    <CVButton v-if="!showInput" outline @click="showInput = true">{{
      t('switchCustomer')
    }}</CVButton>
    <div v-else class="flex items-center justify-start gap-4">
      <SearchUserInput
        :allowed-roles="allowedRoles"
        class="w-full"
        :error="errorMessage"
        @update-user="handleUpdateCustomer"
      />
      <CVButton v-if="customer" variant="warning" @click="showInput = false">{{
        t('cancel')
      }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import SearchUserInput from '@/components/booking-forms/customer/SearchUserInput.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import type { User } from '@/entities/auth/user.entity';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import { ref } from 'vue';

const props = withDefaults(
  defineProps<{
    allowedRoles?: UserRole[];
  }>(),
  {
    allowedRoles: () => [UserRole.CUSTOMER],
  },
);

const { t } = useI18n();

const customer = ref<User>();
const showInput = ref(true);

const { value: customerId, errorMessage } = useField<string | undefined>('customerId');

const handleUpdateCustomer = (selectedCustomer: User | undefined) => {
  customer.value = selectedCustomer ?? customer.value;
  customerId.value = selectedCustomer?.id ?? customerId.value;
  if (selectedCustomer) {
    showInput.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "relatedCustomer": "Related customer",
    "switchCustomer": "Switch customer",
    "customerNumber": "Cnr.",
    "vatId": "VAT ID"
  },
  "de": {
    "relatedCustomer": "Verknüpfte Kunde",
    "switchCustomer": "Kunde wechseln",
    "customerNumber": "Knr.",
    "vatId": "USt-IdNr."
  },
  "es": {
    "relatedCustomer": "Cliente vinculado",
    "switchCustomer": "Cambiar cliente",
    "customerNumber": "N.º cliente",
    "vatId": "NIF-IVA"
  }
}
</i18n>
